import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Pencil, Plus, Power, Printer as PrinterIcon, RefreshCw, ShieldOff, Zap } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Select } from '@/components/ui/field';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  FLEET_PERMISSIONS,
  FLEET_ROUTES,
  PRINT_ERROR_CODES,
  errorCode,
  type FleetBridge,
  type FleetPrinter,
  type IssuedBridgeToken,
} from '../fleetApi';
import { PRINTER_STATUS_VISUALS, PrinterStatusChip, rowStateClass } from '../fleetVisuals';
import {
  apiMessage,
  formatDateTime,
  minutesSince,
  operationKey,
  useAutoRefresh,
  useBranches,
  useNow,
  useQueryParams,
} from '../fleetHelpers';
import PanelResult from './PanelResult';
import PrinterFormDialog from './PrinterFormDialog';
import { BridgeTokenDialog, EnrolBridgeDialog } from './BridgeDialogs';

/** A bridge silent this long has stopped polling, whatever it last said. */
const BRIDGE_STALE_MINUTES = 60;

const REFRESH_MS = 30_000;

/**
 * ## Printers, and the bridges that own them
 *
 * One tab rather than two, because they are one fact: a `BRIDGE_*` printer is
 * unreachable without its agent, so "the printer is fine, the bridge stopped
 * polling" is a sentence an operator can only arrive at with both on screen.
 * ADR-0009 makes the same argument for the device registry — a bridge is a
 * device like any other and belongs in one list, not a second registry beside
 * it — and this is that argument one layer down.
 *
 * **Test print is the diagnostic that closes the loop.** It queues a page that
 * names the printer that produced it, because "it printed" is not the same
 * answer as "the one I meant printed", and it carries an idempotency key
 * because a test page's content is identical every time: without one, the second
 * test of the day would silently return the morning's job and an engineer would
 * conclude the printer was dead when the dedupe was working perfectly.
 *
 * A test print can also be refused with `PRINT_DESTINATION_HAS_NO_PRINTER` — the
 * printer exists but no active destination stands in front of it, so a test page
 * has nowhere to be queued. That is a routing gap, not a hardware fault, and the
 * message says so and points at the routing tab.
 */
export default function PrintersPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();
  const branches = useBranches();
  const now = useNow(30_000);

  const [branchId, setBranchId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FleetPrinter | null>(null);
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [issued, setIssued] = useState<IssuedBridgeToken | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const canViewPrinters = can(FLEET_PERMISSIONS.printersView);
  const canCreate = can(FLEET_PERMISSIONS.printersCreate);
  const canUpdate = can(FLEET_PERMISSIONS.printersUpdate);
  const canDeactivate = can(FLEET_PERMISSIONS.printersDelete);
  const canTestPrint = can(FLEET_PERMISSIONS.printersTestPrint);
  const canViewBridges = can(FLEET_PERMISSIONS.bridgesView);
  const canEnrol = can(FLEET_PERMISSIONS.bridgesEnroll);
  const canRotate = can(FLEET_PERMISSIONS.bridgesRotate);
  const canRevokeBridge = can(FLEET_PERMISSIONS.bridgesRevoke);

  const branchParams = useQueryParams({ branchId });
  const printersQuery = useApi<unknown>(
    canViewPrinters ? FLEET_ROUTES.printers : null,
    useQueryParams({ branchId, limit: 200 }),
  );
  const bridgesQuery = useApi<FleetBridge[]>(
    canViewBridges ? FLEET_ROUTES.bridges : null,
    branchParams,
  );

  const printers = rowsOf<FleetPrinter>(printersQuery.data).rows;
  const bridges = useMemo(() => bridgesQuery.data ?? [], [bridgesQuery.data]);

  const reload = () => {
    printersQuery.reload();
    bridgesQuery.reload();
  };

  useAutoRefresh(reload, { enabled: true, intervalMs: REFRESH_MS });

  const ordered = useMemo(
    () =>
      [...printers].sort((a, b) => {
        const bySeverity =
          PRINTER_STATUS_VISUALS[b.lastStatus].severity -
          PRINTER_STATUS_VISUALS[a.lastStatus].severity;
        if (bySeverity !== 0) return bySeverity;
        return a.name.localeCompare(b.name, i18n.language);
      }),
    [printers, i18n.language],
  );

  const testPrint = async (printer: FleetPrinter) => {
    setBusyId(printer.id);
    const operationId = operationKey('test-print');
    try {
      await api.post(
        FLEET_ROUTES.printerTestPrint(printer.id),
        { operationId, locale: i18n.language },
        { headers: { 'X-Operation-Id': operationId } },
      );
      toast.success(t('fleet.printers.testQueued', { name: printer.name }));
      reload();
    } catch (error) {
      if (errorCode(error) === PRINT_ERROR_CODES.destinationHasNoPrinter) {
        toast.error(t('fleet.printers.testNoDestination', { name: printer.name }));
      } else {
        toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
      }
    } finally {
      setBusyId(null);
    }
  };

  const deactivate = async (printer: FleetPrinter) => {
    const ok = await confirm.confirm({
      title: t('fleet.printers.deactivateTitle'),
      body: t('fleet.printers.deactivateConfirm', { name: printer.name }),
      confirmLabel: t('fleet.printers.deactivate'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(FLEET_ROUTES.printer(printer.id));
      toast.success(t('fleet.printers.deactivated'));
      reload();
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    }
  };

  const reactivate = async (printer: FleetPrinter) => {
    try {
      await api.patch(FLEET_ROUTES.printer(printer.id), { isActive: true });
      toast.success(t('fleet.printers.reactivated'));
      reload();
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    }
  };

  const rotate = async (bridge: FleetBridge) => {
    const ok = await confirm.confirm({
      title: t('fleet.bridges.rotateTitle'),
      body: t('fleet.bridges.rotateConfirm', { name: bridge.name }),
      confirmLabel: t('fleet.bridges.rotate'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const { data } = await api.post<IssuedBridgeToken>(FLEET_ROUTES.bridgeToken(bridge.id));
      setIssued(data);
      reload();
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    }
  };

  const revokeBridge = async (bridge: FleetBridge) => {
    const reason = await confirm.prompt({
      title: t('fleet.bridges.revokeTitle', { name: bridge.name }),
      body: t('fleet.bridges.revokeConfirm', { count: bridge.printers.length }),
      label: t('fleet.bridges.revokeReason'),
      confirmLabel: t('fleet.bridges.revoke'),
      tone: 'danger',
    });
    if (reason === null) return;
    try {
      await api.post(
        FLEET_ROUTES.bridgeRevoke(bridge.id),
        reason.trim() ? { reason: reason.trim() } : {},
      );
      toast.success(t('fleet.bridges.revoked'));
      reload();
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            aria-label={t('fleet.common.branch')}
          >
            <option value="">{t('fleet.common.allBranches')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        )}
        <Button variant="outline" size="sm" onClick={reload}>
          <Icon as={RefreshCw} />
          {t('fleet.common.refresh')}
        </Button>
        <div className="ms-auto flex flex-wrap gap-2">
          {canEnrol && (
            <Button variant="outline" onClick={() => setEnrolOpen(true)}>
              <Icon as={Plus} />
              {t('fleet.bridges.enrol')}
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Icon as={Plus} />
              {t('fleet.printers.add')}
            </Button>
          )}
        </div>
      </div>

      {canViewPrinters && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">{t('fleet.printers.heading')}</h2>
          <PanelResult
            loading={printersQuery.loading && !printersQuery.data}
            error={printersQuery.error}
            count={ordered.length}
            onRetry={reload}
            subject={t('fleet.printers.subject')}
            filtered={Boolean(branchId)}
            onClearFilters={() => setBranchId('')}
            emptyTitle={t('fleet.printers.emptyTitle')}
            emptyHint={t('fleet.printers.emptyHint')}
            emptyAction={
              canCreate ? (
                <Button
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  <Icon as={Plus} />
                  {t('fleet.printers.add')}
                </Button>
              ) : undefined
            }
          >
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('fleet.common.printer')}</TH>
                    <TH>{t('fleet.common.branch')}</TH>
                    <TH>{t('fleet.printers.connection')}</TH>
                    <TH>{t('fleet.common.bridge')}</TH>
                    <TH>{t('fleet.common.status')}</TH>
                    <TH>{t('fleet.printers.lastReport')}</TH>
                    <TH className="text-end">{t('fleet.common.actions')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {ordered.map((printer) => {
                    const visual = PRINTER_STATUS_VISUALS[printer.lastStatus];
                    return (
                      <TR
                        key={printer.id}
                        className={cn(
                          rowStateClass(printer.isActive ? visual : undefined),
                          !printer.isActive && 'opacity-60',
                        )}
                      >
                        <TD>
                          <span className="font-medium">{printer.name}</span>
                          <p className="text-xs text-ink-muted">{printer.code}</p>
                          {!printer.isActive && (
                            <Badge tone="neutral" className="mt-1">
                              {t('fleet.printers.outOfService')}
                            </Badge>
                          )}
                        </TD>
                        <TD>{printer.branch?.name ?? t('fleet.common.dash')}</TD>
                        <TD>
                          <span>{t(`fleet.connection.${printer.connection}`)}</span>
                          <p className="text-xs text-ink-muted">
                            {printer.address
                              ? `${printer.address}${printer.port ? `:${printer.port}` : ''}`
                              : t('fleet.printers.noAddress')}
                          </p>
                        </TD>
                        <TD>
                          {printer.bridge ? (
                            printer.bridge.name
                          ) : (
                            <span className="text-ink-muted">{t('fleet.printers.noBridge')}</span>
                          )}
                        </TD>
                        <TD>
                          <PrinterStatusChip status={printer.lastStatus} />
                          {printer.lastStatusDetail && (
                            <p className="mt-1 max-w-[14rem] text-xs text-ink-muted">
                              {printer.lastStatusDetail}
                            </p>
                          )}
                        </TD>
                        <TD className="text-ink-muted">
                          {printer.lastStatusAt
                            ? formatDateTime(printer.lastStatusAt, i18n.language)
                            : t('fleet.printers.neverReported')}
                        </TD>
                        <TD>
                          <div className="flex flex-wrap justify-end gap-1">
                            {canTestPrint && printer.isActive && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busyId === printer.id}
                                onClick={() => void testPrint(printer)}
                              >
                                <Icon as={Zap} />
                                {t('fleet.printers.testPrint')}
                              </Button>
                            )}
                            {canUpdate && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title={t('common.edit')}
                                onClick={() => {
                                  setEditing(printer);
                                  setFormOpen(true);
                                }}
                              >
                                <Icon as={Pencil} />
                                <span className="sr-only">{t('common.edit')}</span>
                              </Button>
                            )}
                            {canDeactivate && printer.isActive && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title={t('fleet.printers.deactivate')}
                                onClick={() => void deactivate(printer)}
                              >
                                <Icon as={Power} className="text-red-600 dark:text-red-400" />
                                <span className="sr-only">{t('fleet.printers.deactivate')}</span>
                              </Button>
                            )}
                            {canUpdate && !printer.isActive && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => void reactivate(printer)}
                              >
                                <Icon as={Power} />
                                {t('fleet.printers.reactivate')}
                              </Button>
                            )}
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          </PanelResult>
        </section>
      )}

      {canViewBridges && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">{t('fleet.bridges.heading')}</h2>
          <p className="text-sm text-ink-muted">{t('fleet.bridges.explainer')}</p>
          <PanelResult
            loading={bridgesQuery.loading && !bridgesQuery.data}
            error={bridgesQuery.error}
            count={bridges.length}
            onRetry={reload}
            subject={t('fleet.bridges.subject')}
            filtered={Boolean(branchId)}
            onClearFilters={() => setBranchId('')}
            emptyTitle={t('fleet.bridges.emptyTitle')}
            emptyHint={t('fleet.bridges.emptyHint')}
            emptyAction={
              canEnrol ? (
                <Button onClick={() => setEnrolOpen(true)}>
                  <Icon as={Plus} />
                  {t('fleet.bridges.enrol')}
                </Button>
              ) : undefined
            }
          >
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('fleet.common.bridge')}</TH>
                    <TH>{t('fleet.common.branch')}</TH>
                    <TH>{t('fleet.bridges.agentVersion')}</TH>
                    <TH>{t('fleet.bridges.lastPoll')}</TH>
                    <TH>{t('fleet.bridges.owns')}</TH>
                    <TH className="text-end">{t('fleet.common.actions')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {bridges.map((bridge) => {
                    const silentFor = minutesSince(bridge.lastSeenAt, now);
                    /* Null counts as stale: a bridge enrolled and never started
                       is exactly as broken as one that stopped, and treating
                       "never" as "fine" is how a half-finished install ships. */
                    const stale =
                      bridge.isActive && (silentFor === null || silentFor >= BRIDGE_STALE_MINUTES);
                    return (
                      <TR
                        key={bridge.id}
                        className={cn(
                          'border-s-4',
                          !bridge.isActive
                            ? 'border-s-slate-400 opacity-60'
                            : stale
                              ? 'border-s-red-500 bg-red-50/40 dark:bg-red-950/20'
                              : 'border-s-transparent',
                        )}
                      >
                        <TD>
                          <span className="font-medium">{bridge.name}</span>
                          {!bridge.isActive && (
                            <p className="text-xs text-ink-muted">
                              {bridge.revokedReason ?? t('fleet.bridges.revokedStatus')}
                            </p>
                          )}
                          {bridge.isActive && stale && (
                            <p className="text-xs text-red-700 dark:text-red-400">
                              {t('fleet.bridges.notPolling')}
                            </p>
                          )}
                        </TD>
                        <TD>
                          {branches.find((branch) => branch.id === bridge.branchId)?.name ??
                            t('fleet.common.dash')}
                        </TD>
                        <TD>{bridge.agentVersion ?? t('fleet.common.dash')}</TD>
                        <TD className="text-ink-muted">
                          {bridge.lastSeenAt
                            ? formatDateTime(bridge.lastSeenAt, i18n.language)
                            : t('fleet.common.never')}
                        </TD>
                        <TD>
                          {bridge.printers.length === 0 ? (
                            <span className="text-ink-muted">{t('fleet.bridges.ownsNone')}</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {bridge.printers.map((printer) => (
                                <Badge key={printer.id} tone="neutral" className="gap-1">
                                  <Icon as={PrinterIcon} size="sm" aria-hidden />
                                  {printer.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </TD>
                        <TD>
                          <div className="flex flex-wrap justify-end gap-1">
                            {canRotate && bridge.isActive && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title={t('fleet.bridges.rotate')}
                                onClick={() => void rotate(bridge)}
                              >
                                <Icon as={KeyRound} className="text-accent" />
                                <span className="sr-only">{t('fleet.bridges.rotate')}</span>
                              </Button>
                            )}
                            {canRevokeBridge && bridge.isActive && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title={t('fleet.bridges.revoke')}
                                onClick={() => void revokeBridge(bridge)}
                              >
                                <Icon as={ShieldOff} className="text-red-600 dark:text-red-400" />
                                <span className="sr-only">{t('fleet.bridges.revoke')}</span>
                              </Button>
                            )}
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          </PanelResult>
        </section>
      )}

      <PrinterFormDialog
        open={formOpen}
        printer={editing}
        branches={branches}
        bridges={bridges}
        defaultBranchId={branchId || undefined}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
      />
      <EnrolBridgeDialog
        open={enrolOpen}
        branches={branches}
        defaultBranchId={branchId || undefined}
        onClose={() => setEnrolOpen(false)}
        onEnrolled={(data) => {
          setIssued(data);
          reload();
        }}
      />
      <BridgeTokenDialog issued={issued} onClose={() => setIssued(null)} />
    </div>
  );
}
